import React from 'react';
import VideoProviderSelector from '../selectors/VideoProviderSelector';
import { Button } from '../ui';
import { SwitchField } from './ReviewerSettingsSection';
import {
  MAX_VIDEO_MODELS,
  PER_SHOT_VIDEO_SETTINGS,
  VideoGenerationSettings,
  VideoModelEntry,
} from '../../api/settings';
import { MediaOutputSpec } from '../../api/structs/media-spec';
import { VideoProvider } from '../../api/structs/providers';

/** How the field changes the set: the form hands it the current one and takes the next. */
export type EditVideo = (change: (video: VideoGenerationSettings) => VideoGenerationSettings) => void;

const defaultIndex = (video: VideoGenerationSettings): number =>
  Math.max(0, video.models.findIndex((entry) => entry.model === video.default));

/** Changes one row. The default follows its row to a new model rather than staying behind on the old id. */
const changeRow = (
  video: VideoGenerationSettings,
  index: number,
  change: (entry: VideoModelEntry) => VideoModelEntry
): VideoGenerationSettings => {
  const entry = video.models[index];
  if (!entry) return video;
  const next = change(entry);
  return {
    ...video,
    models: video.models.map((current, i) => (i === index ? next : current)),
    default: defaultIndex(video) === index ? next.model : video.default,
  };
};

/**
 * A new row starts on the last row's provider; the selector fills in a model
 * once that provider's catalog answers, one no other row holds when there is one.
 */
const addRow = (video: VideoGenerationSettings): VideoGenerationSettings => {
  if (video.models.length >= MAX_VIDEO_MODELS) return video;
  const provider = video.models[video.models.length - 1]?.provider ?? 'vertexai';
  return { ...video, models: [...video.models, { provider, model: '', modes: [] }] };
};

const removeRow = (video: VideoGenerationSettings, index: number): VideoGenerationSettings => {
  if (video.models.length <= 1) return video;
  const models = video.models.filter((_, i) => i !== index);
  return { ...video, models, default: defaultIndex(video) === index ? models[0].model : video.default };
};

/** Stable keys for the rows, so a removed row does not hand its selector (and its pending edits) to the next one. */
const useRowKeys = (count: number): number[] => {
  const keys = React.useRef<number[]>([]);
  const next = React.useRef(0);
  while (keys.current.length < count) keys.current.push(next.current++);
  keys.current.length = count;
  return keys.current;
};

interface RowProps {
  index: number;
  entry: VideoModelEntry;
  isDefault: boolean;
  canRemove: boolean;
  groupName: string;
  /** The models the other rows hold: a new row starts on one of these only when the catalog has no other. */
  otherModels: readonly string[];
  onEdit: EditVideo;
  onRemove: (index: number) => void;
}

const VideoModelRow: React.FC<RowProps> = ({ index, entry, isDefault, canRemove, groupName, otherModels, onEdit, onRemove }) => {
  // Stable per row: the selector lists its callbacks as effect dependencies.
  const handlers = React.useMemo(() => {
    const update = (change: (current: VideoModelEntry) => VideoModelEntry) =>
      onEdit((video) => changeRow(video, index, change));
    return {
      // A new provider has no model yet, and the saved modes were the old model's: unknown until saved.
      onProviderChange: (provider: string) =>
        update((current) => (current.provider === provider ? current : { provider, model: '', modes: [] })),
      onModelChange: (model: string) =>
        update((current) => (current.model === model ? current : { ...current, model, modes: [] })),
      onSettingsChange: (settings: Partial<MediaOutputSpec>) =>
        update((current) => ({ ...current, settings })),
      makeDefault: () => onEdit((video) => ({ ...video, default: video.models[index]?.model ?? video.default })),
    };
  }, [index, onEdit]);
  const n = index + 1;
  // Every saved model takes some mode, so an empty list is "not known yet", not "takes none".
  const noReferences = entry.modes.length > 0 && !entry.modes.includes('references');

  return (
    <div
      data-testid="video-model-row"
      className="flex flex-col gap-2 rounded border border-[var(--app-border)] bg-[var(--app-surface-subtle)] p-2"
    >
      <VideoProviderSelector
        provider={entry.provider as VideoProvider}
        model={entry.model}
        settings={(entry.settings ?? {}) as Partial<MediaOutputSpec>}
        providerAriaLabel={`Video provider ${n}`}
        modelAriaLabel={`Video model ${n}`}
        settingsAriaLabel={`Video model ${n} settings`}
        hiddenSettingsFields={PER_SHOT_VIDEO_SETTINGS}
        avoidModels={otherModels}
        onProviderChange={handlers.onProviderChange}
        onModelChange={handlers.onModelChange}
        onSettingsChange={handlers.onSettingsChange}
      />
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 text-xs text-[var(--app-text)]">
          <input
            type="radio"
            name={groupName}
            aria-label={`Default video model ${n}`}
            checked={isDefault}
            onChange={handlers.makeDefault}
          />
          Default
        </label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Remove video model ${n}`}
          disabled={!canRemove}
          onClick={() => onRemove(index)}
        >
          Remove
        </Button>
      </div>
      {noReferences && <p className="text-[11px] text-[var(--app-text-muted)]">Takes no reference pictures</p>}
    </div>
  );
};

/**
 * The video models the user allows, one row each, with the one a clip runs on
 * when the director names none, and the switch for reference mode. What each
 * model takes (frames, reference pictures) is the server's to say, so it is
 * only shown, under a saved row.
 */
const VideoModelsField: React.FC<{ video: VideoGenerationSettings; onEdit: EditVideo }> = ({ video, onEdit }) => {
  const groupName = React.useId();
  const keys = useRowKeys(video.models.length);
  const chosen = defaultIndex(video);

  const remove = React.useCallback(
    (index: number) => {
      keys.splice(index, 1);
      onEdit((current) => removeRow(current, index));
    },
    [keys, onEdit]
  );

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-1">Video models</h4>
        <p className="text-[11px] text-[var(--app-text-muted)]">The director picks one for each clip.</p>
      </div>
      {video.models.map((entry, index) => (
        <VideoModelRow
          key={keys[index]}
          index={index}
          entry={entry}
          isDefault={index === chosen}
          canRemove={video.models.length > 1}
          groupName={groupName}
          otherModels={video.models.filter((_, i) => i !== index).map((other) => other.model)}
          onEdit={onEdit}
          onRemove={remove}
        />
      ))}
      <div className="flex justify-start">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={video.models.length >= MAX_VIDEO_MODELS}
          onClick={() => onEdit(addRow)}
        >
          Add a video model
        </Button>
      </div>
      <p className="text-[11px] text-[var(--app-text-muted)]">
        Duration is chosen per shot, so it is not part of these settings.
      </p>
      <SwitchField
        label="Reference mode (video from reference images)"
        checked={video.reference_mode}
        onToggle={() => onEdit((current) => ({ ...current, reference_mode: !current.reference_mode }))}
      />
      {!video.reference_mode && (
        <p className="text-[11px] text-[var(--app-text-muted)]">Off: no shot can be made from reference images.</p>
      )}
    </div>
  );
};

export default VideoModelsField;
